/**
 * GuestShell — the frame of the public pages (homepage, course catalogue, course details):
 * header with the tenant logo, links, language switcher, theme toggle and Sign in, plus a footer
 * (logo, tagline, legal links and the homepage's optional Help / Contact links).
 * Links are "Home", then — while the public catalogue is on — "Learning catalogue", "Programs" and
 * "Communities" (its tabs), then the page's own: section anchors ("#about") or routes. With `overHero` the header floats
 * transparently on the hero until the page scrolls.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axiosInstance from '@/api/axiosInstance';
import { API_PREFIX } from '@/api/endpoints/api.constants';
import { ChevronDown, Globe, Menu, Moon, Sun, X } from 'lucide-react';
import { useLanguage, tr } from '@/i18n';
import { useTheme, type Theme } from '@/contexts/shared/ThemeContext';
import { usePublicCourses } from '@/components/widgets/Landing/publicCatalog';
import styles from './GuestShell.module.css';

import logoIconDark from '../../../public/assets/logos/logo-white.svg';
import logoIconLight from '../../../public/assets/logos/logo-full.svg';

interface InstalledLang { code: string; name: string; flag: string }
export interface GuestNavItem { label: string; href: string }
export interface GuestFooter { tagline?: string; helpUrl?: string; contactUrl?: string }

export const guestTenantHeader = () => ({ 'X-Tenant-ID': localStorage.getItem('tenant_id') || '1' });

const isOutside = (href: string) => /^(https?:|mailto:)/i.test(href);
/** "help@company.com" → a mailto: link; a web address or a page of this site as given; anything else is dropped. */
const footerHref = (v: string | undefined) => {
  const t = (v ?? '').trim();
  if (/^[^\s@:/]+@[^\s@]+$/.test(t)) return `mailto:${t}`;
  return /^(https?:\/\/|mailto:|\/(?!\/))/i.test(t) ? t : '';
};

const FooterLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (isOutside(href)
  ? <a href={href} {...(/^https?:/i.test(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{children}</a>
  : <Link to={href}>{children}</Link>);

export const GuestShell: React.FC<{ nav?: GuestNavItem[]; overHero?: boolean; footer?: GuestFooter; children: React.ReactNode }> = ({ nav = [], overHero: heroFirst = false, footer, children }) => {
  const { locale, setLocale } = useLanguage();
  const { theme, setTheme, allowedThemes } = useTheme();
  const { pathname, hash, search } = useLocation();
  // The same small request as the homepage's featured courses (shared), not the full catalogue.
  const catalog = usePublicCourses();

  const [languages, setLanguages] = useState<InstalledLang[]>([]);
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLightUrl, setLogoLightUrl] = useState<string | null>(null);
  const [brandName, setBrandName] = useState<string>('iLearn');

  useEffect(() => {
    const headers = guestTenantHeader();
    axiosInstance
      .get(`${API_PREFIX}/languages/`, { headers })
      .then((res) => { if (Array.isArray(res.data)) setLanguages(res.data); })
      .catch(() => {});
    axiosInstance
      // ?assets=url: logos as small cacheable URLs, not the ~5 MB inline payload.
      .get(`${API_PREFIX}/tenants/branding/?assets=url`, { headers })
      .then((res) => {
        const b = res.data;
        if (!b || typeof b !== 'object') return;
        if (b.logo_url) setLogoUrl(b.logo_url);
        if (b.logo_light_url) setLogoLightUrl(b.logo_light_url);
        if (b._tenant_name) setBrandName(b._tenant_name);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // A section link ("#faq") — clicked, or opened from a shared URL — can point at a block that has not
  // rendered yet (blocks load on demand); the browser then has nothing to scroll to. Scroll once it exists.
  useEffect(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (!id) return undefined;
    let tries = 0;
    let timer = 0;
    const find = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      else if (tries++ < 50) timer = window.setTimeout(find, 100);
    };
    timer = window.setTimeout(find, 0);
    return () => window.clearTimeout(timer);
  }, [hash]);

  const links: GuestNavItem[] = [{ label: tr('guest.home', 'Home'), href: '/' }];
  if (catalog) {
    links.push(
      { label: tr('guest.learning_catalog', 'Learning catalogue'), href: '/catalog' },
      { label: tr('landing.stat_programs', 'Programs'), href: '/catalog?type=program' },
      { label: tr('catalog.type_communities', 'Communities'), href: '/catalog?type=community' },
    );
  }
  // The page's own links, without repeating a built-in one (same target or same label, e.g. a hero's "Home").
  for (const l of nav) {
    const label = l.label.trim().toLocaleLowerCase();
    if (!links.some((b) => b.href === l.href || b.label.toLocaleLowerCase() === label)) links.push(l);
  }

  const overHero = heroFirst && !scrolled && !menuOpen;
  const activeLogo = theme === 'light' && !overHero ? (logoLightUrl || logoUrl || logoIconLight) : (logoUrl || logoIconDark);
  const canToggleTheme = allowedThemes.length > 1;
  const nextTheme: Theme = theme === 'dark' ? 'light' : 'dark';
  const otherLangs = languages.filter((l) => l.code !== locale);
  // Catalogue links: "/catalog?type=program" is active on that tab only; plain "/catalog" on the courses tab
  // and on a course's page (/catalog/course/…) — never several at once.
  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    if (!href.startsWith('/')) return false;
    const [path, query = ''] = href.split('?');
    const linkType = new URLSearchParams(query).get('type');
    if (path === '/catalog') {
      if (pathname === '/catalog') return linkType === new URLSearchParams(search).get('type');
      return pathname.startsWith('/catalog/') && !linkType;
    }
    return pathname === path || pathname.startsWith(`${path}/`);
  };
  const footerLogo = theme === 'light' ? (logoLightUrl || logoUrl || logoIconLight) : (logoUrl || logoIconDark);
  const currentLang = languages.find((l) => l.code === locale);
  const helpHref = footerHref(footer?.helpUrl);
  const contactHref = footerHref(footer?.contactUrl);

  return (
    <div className={styles.page} onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 8)}>
      <a href="#main" className={styles.skip}>{tr('guest.skip_to_content', 'Skip to content')}</a>
      <header className={`${styles.header} ${scrolled ? styles.headerScrolled : ''} ${overHero ? styles.headerOverHero : ''}`}>
        <Link to="/" className={styles.brand} aria-label={brandName}>
          <img src={activeLogo} alt={brandName} className={styles.logo} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </Link>

        {links.length > 0 && (
          <nav className={`${styles.nav} ${menuOpen ? styles.navOpen : ''}`} aria-label={tr('guest.sections', 'Sections')}>
            {links.map((l) => (l.href.startsWith('#')
              ? <a key={l.href} href={l.href} className={styles.navLink} onClick={() => setMenuOpen(false)}>{l.label}</a>
              : (
                <Link
                  key={l.href}
                  to={l.href}
                  className={`${styles.navLink} ${isActive(l.href) ? styles.navLinkActive : ''}`}
                  aria-current={isActive(l.href) ? 'page' : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  {l.label}
                </Link>
              )))}
          </nav>
        )}

        <div className={styles.actions}>
          {otherLangs.length > 0 && (
            <div className={styles.dropdownWrap} ref={langRef}>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setLangOpen((v) => !v)}
                aria-label={`${tr('guest.language', 'Language')}: ${currentLang?.name ?? locale}`}
                aria-expanded={langOpen}
                aria-haspopup="menu"
              >
                <Globe size={18} aria-hidden="true" />
                <span className={styles.langCode}>{locale.split(/[-_]/)[0].toUpperCase()}</span>
                <ChevronDown size={16} aria-hidden="true" />
              </button>
              {langOpen && (
                <div className={styles.dropdown} role="menu">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      role="menuitemradio"
                      aria-checked={locale === lang.code}
                      lang={lang.code}
                      className={`${styles.dropdownItem} ${locale === lang.code ? styles.dropdownItemActive : ''}`}
                      onClick={() => { setLangOpen(false); setLocale(lang.code); }}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {canToggleTheme && (
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => setTheme(nextTheme)}
              aria-label={theme === 'dark' ? tr('ui.light_mode', 'Light mode') : tr('ui.dark_mode', 'Dark mode')}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          )}

          <Link to="/login" className={styles.loginBtn}>{tr('ui.sign_in', 'Sign in')}</Link>
          {links.length > 0 && (
            <button type="button" className={`${styles.iconBtn} ${styles.burger}`} onClick={() => setMenuOpen((v) => !v)} aria-label={tr('guest.menu', 'Menu')} aria-expanded={menuOpen}>
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          )}
        </div>
      </header>

      <main id="main" className={`${styles.content} ${heroFirst ? styles.contentOverHero : ''}`}>
        {children}
      </main>

      <footer className={styles.footer}>
        <span className={styles.footerBrand}>
          <Link to="/" aria-label={brandName}>
            <img src={footerLogo} alt={brandName} className={styles.footerLogo} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </Link>
          <span className={styles.footerTagline}>{footer?.tagline || tr('guest.footer_tagline', 'Learning today. A stronger tomorrow.')}</span>
        </span>
        <nav className={styles.footerLinks} aria-label={tr('guest.footer_links', 'Site links')}>
          <Link to="/legal/privacy">{tr('legal.privacy_title', 'Privacy Policy')}</Link>
          <Link to="/legal/terms">{tr('legal.terms_title', 'Terms of Use')}</Link>
          <Link to="/legal/cookies">{tr('legal.cookies_title', 'Cookie Policy')}</Link>
          {helpHref && <FooterLink href={helpHref}>{tr('guest.help', 'Help')}</FooterLink>}
          {contactHref && <FooterLink href={contactHref}>{tr('guest.contact', 'Contact')}</FooterLink>}
        </nav>
      </footer>
    </div>
  );
};

/** Full-page spinner in the shell's page frame (while a public page loads). */
export const GuestLoading: React.FC = () => (
  <div className={styles.page}><div className={styles.loadingWrap}><div className={styles.spinner} /></div></div>
);

export default GuestShell;
